import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import { PARCEL_STATUS } from '../constants';

// Query keys
export const PARCELS_QUERY_KEYS = {
  all: ['parcels'],
  list: (userId) => ['parcels', 'list', userId],
  active: (userId) => ['parcels', 'active', userId],
  history: (userId) => ['parcels', 'history', userId],
  detail: (parcelId) => ['parcels', 'detail', parcelId],
};

// Active statuses for filtering
const ACTIVE_STATUSES = [
  PARCEL_STATUS.PENDING?.value || 'PENDING',
  PARCEL_STATUS.ASSIGNED?.value || 'ASSIGNED',
  PARCEL_STATUS.PICKED_UP?.value || 'PICKED_UP',
  PARCEL_STATUS.IN_TRANSIT?.value || 'IN_TRANSIT',
  PARCEL_STATUS.OUT_FOR_DELIVERY?.value || 'OUT_FOR_DELIVERY',
];

/**
 * Shared hook for fetching parcels list
 * Uses React Query for caching to prevent duplicate API calls
 *
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query
 * @param {number} options.staleTime - Stale time in ms (default 2 minutes)
 * @returns {Object} Query result with data, loading, error states
 */
export function useParcelsQuery(options = {}) {
  const { makeApiCall } = useApiCall();
  const userId = useSelector((state) => state.userProfile.data?.user?.userId);
  const { enabled = true, staleTime = 2 * 60 * 1000 } = options;

  return useQuery({
    queryKey: PARCELS_QUERY_KEYS.list(userId),
    queryFn: async () => {
      const apiConfig = buildApiConfig(endPointEnums.GET_PARCELS_LIST, {
        userId,
      });
      const response = await makeApiCall(apiConfig);
      const parcels = response?.data?.parcels || response?.data || [];

      // Normalize parcels data
      return parcels.map((p) => {
        const riderId = p.riderId;
        const rawRl = p.riderLocation;
        let riderLocation = null;
        if (rawRl) {
          riderLocation = { lat: rawRl.lat, lng: rawRl.lng };
          if (riderLocation.lat == null || riderLocation.lng == null) {
            riderLocation = null;
          }
        }
        return { ...p, riderId, riderLocation };
      });
    },
    enabled: enabled && !!userId,
    staleTime,
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Hook for active parcels only
 * Filters from the main parcels query
 */
export function useActiveParcelsQuery(options = {}) {
  const parcelsQuery = useParcelsQuery(options);

  const activeParcels =
    parcelsQuery.data?.filter((p) => {
      const status = (p.parcelStatus || '').toString().toLowerCase();
      return ACTIVE_STATUSES.map((s) => s.toString().toLowerCase()).includes(status);
    }) || [];

  return {
    ...parcelsQuery,
    data: activeParcels,
  };
}

/**
 * Hook for parcels history (completed/cancelled)
 * Filters from the main parcels query
 */
export function useParcelsHistoryQuery(options = {}) {
  const parcelsQuery = useParcelsQuery(options);

  const historyParcels =
    parcelsQuery.data?.filter((p) => {
      const status = (p.parcelStatus || '').toString().toLowerCase();
      return !ACTIVE_STATUSES.map((s) => s.toString().toLowerCase()).includes(status);
    }) || [];

  return {
    ...parcelsQuery,
    data: historyParcels,
  };
}

/**
 * Hook to invalidate parcels cache
 * Use after creating, updating, or deleting a parcel
 */
export function useInvalidateParcels() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: PARCELS_QUERY_KEYS.all }),
    invalidateList: (userId) =>
      queryClient.invalidateQueries({
        queryKey: PARCELS_QUERY_KEYS.list(userId),
      }),
  };
}

export default useParcelsQuery;
